import Request from 'redux/config/services/Request'
import {
  OnboardingStage,
  SectionType,
  WorkforceSyncType,
} from './onboardingSlice'
import axios from 'axios'
import { v4 as uuidv4 } from 'uuid'
import { KycCsvFormData } from './dashboard/KYCCsv'

export interface CommuterBenefitSetupFormData {
  transitExists: boolean | null
  transitName: string
  parkingExists: boolean | null
  parkingName: string
  transitSetupAction?: 'register' | 'create' | null
  parkingSetupAction?: 'register' | 'create' | null
  draft?: boolean
}

const updateOnboardingStageService = (stage: OnboardingStage) => {
  return Request.patch('/hr/employer/onboarding/', { stage: stage })
}

const updateWorkforceSyncTypeService = (type: WorkforceSyncType) => {
  return Request.patch('/hr/employer/onboarding/', { workforceSyncType: type })
}

const updateOnboardingSectionStatusService = (
  sectionType: SectionType,
  status: boolean
) => {
  const url = `/hr/employer/onboarding/section/${sectionType}/`
  return Request.patch(url, { status: status })
}

const getAdditionalDetailsDownloadUrlService = (fileName: string) => {
  return Request.post(
    '/hr/employer/onboarding/get_additional_details_download_url/',
    {
      fileName: fileName,
    }
  )
}

const getAdditionalDetailsUploadUrlService = (
  fileName: string,
  contentType: string
) => {
  return Request.post(
    '/hr/employer/onboarding/get_additional_details_upload_url/',
    {
      fileName: fileName,
      contentType: contentType,
    }
  )
}

export const uploadFile = async (url: string, file: File) => {
  const response = await axios.put(url, file, {
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
    },
    withCredentials: false,
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
    transformRequest: [(data) => data],
  })
  return response
}

export const uploadAdditionalDocuments = async (file: File) => {
  try {
    const fileName = uuidv4()
    const response = await getAdditionalDetailsUploadUrlService(
      fileName,
      file.type
    )
    if (!response?.data?.uploadUrl) {
      return { status: 'error', error: 'No upload URL returned' }
    }
    const uploadUrl = response.data.uploadUrl
    const uploadResponse = await uploadFile(uploadUrl, file)
    if (uploadResponse.status !== 200) {
      return { status: 'error', error: 'Upload failed' }
    }
    const receiptUrl = await getAdditionalDetailsDownloadUrlService(fileName)
    return { status: 'success', downloadUrl: receiptUrl.data.downloadUrl }
  } catch (error) {
    return { status: 'error', error: error }
  }
}

const getAllOnboardingSectionsService = async () => {
  const response = await Request.get('/hr/employer/onboarding/section/')
  return response.data
}

const createCsvKycDetailsService = (kycDetails: KycCsvFormData) => {
  return Request.post('/hr/employer/onboarding/kyc_csv_details/', kycDetails)
}

const getCommuterBenefitSetupService = () => {
  return Request.get('/hr/employer/onboarding/commuter_benefit_setup/')
}

const saveCommuterBenefitSetupService = (
  payload: CommuterBenefitSetupFormData
) => {
  return Request.post(
    '/hr/employer/onboarding/commuter_benefit_setup/',
    payload
  )
}

export default {
  updateOnboardingStageService,
  updateWorkforceSyncTypeService,
  updateOnboardingSectionStatusService,
  getAdditionalDetailsDownloadUrlService,
  getAdditionalDetailsUploadUrlService,
  getAllOnboardingSectionsService,
  createCsvKycDetailsService,
  getCommuterBenefitSetupService,
  saveCommuterBenefitSetupService,
}
